'use client';

import { useRef, useState } from 'react';
import { useElementErrorMsg, useFetch } from '@/app/hooks';
import type { ImageFormState, ImagePrompt, TextFormState, VideoFormState, VideoPrompt } from '@/app/types';
import { convertAssets2Components } from '@/app/utils';
import { materialsPreProcessElement } from '../apis';
import type { MaterialsPreProcessElementData, MaterialV1Component } from '../types';

interface UsePreCreateProps {
  state: ImageFormState | VideoFormState | TextFormState;
  isVideoMode?: boolean;
  onStateUpdate?: (updates: Partial<ImageFormState | VideoFormState | TextFormState>) => void;
  onStepChange?: (step: number) => void;
  initialStep?: number;
}

/**
 * 管理预创建（pre-create）相关的逻辑
 * 包括：资源差异检测、是否需要重新生成描述、执行预创建操作、步骤管理
 */
export const usePreCreate = ({
  state,
  isVideoMode = false,
  onStateUpdate,
  onStepChange,
  initialStep = 1,
}: UsePreCreateProps) => {
  const { showErrorMsg } = useElementErrorMsg();
  const [step, setStep] = useState<number>(initialStep);
  const tempAssetSourceRef = useRef<
    {
      selectedImage?: string;
      sourceImage?: string;
      video?: string;
    }[]
  >([]);

  const updateStep = (newStep: number) => {
    setStep(newStep);
    onStepChange?.(newStep);
  };

  /**
   * 从 optionalAssets 更新 tempAssetSourceRef
   * @param optionalAssets - 可选的资源数组
   */
  const updateCurrentAssets = (optionalAssets?: (ImagePrompt | VideoPrompt | null)[]): void => {
    tempAssetSourceRef.current =
      optionalAssets?.map((it) => {
        if (!it) return {};
        if ('sourceImage' in it) {
          return {
            selectedImage: it.selectedImage,
            sourceImage: it.sourceImage,
          };
        }
        if ('video' in it) {
          return {
            video: it.video,
          };
        }
        return {};
      }) ?? [];
  };

  /**
   * 检查指定索引的资源是否有变化
   * @param idx - 资源索引
   * @returns 是否有变化
   */
  const isAssetPromptDiff = (idx: number): boolean => {
    if (!('optionalAssets' in state)) {
      return false;
    }
    const asset = state.optionalAssets?.[idx];
    const {
      selectedImage: preSelectedImage,
      sourceImage: preSourceImage,
      video: preVideo,
    } = tempAssetSourceRef.current?.[idx] ?? {};

    // 当前为空，如果 pre 有值，则认为有差异
    if (!asset) {
      return !!preSelectedImage || !!preSourceImage || !!preVideo;
    }

    // 当前为图片，sourceImage 或 selectedImage 有差异都认为有差异
    if ('sourceImage' in asset) {
      return asset.sourceImage !== preSourceImage || preSelectedImage !== asset.selectedImage;
    }

    // 当前为视频，video 有差异认为有差异
    if ('video' in asset) {
      return asset.video !== preVideo;
    }

    return false;
  };

  /**
   * 判断是否需要重新生成描述
   * @returns 是否需要重新生成描述
   */
  const needRecaption = (): boolean => {
    if (!('optionalAssets' in state)) {
      return false;
    }
    return state.optionalAssets?.some((_, idx) => isAssetPromptDiff(idx)) ?? false;
  };

  /**
   * 执行预创建操作
   * @param params - 预创建参数
   */
  const { run: doPreCreate, loading: preCreateLoading } = useFetch(
    (params: Omit<MaterialsPreProcessElementData, 'url'>) => materialsPreProcessElement(params),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          showErrorMsg(error);
        } else {
          onStateUpdate?.({
            ...data,
            ...data.recaption,
          } as Partial<ImageFormState | VideoFormState>);
          if ('optionalAssets' in state) {
            updateCurrentAssets(state.optionalAssets);
          }
          updateStep(2);
        }
      },
    },
  );

  /**
   * 执行预创建操作（带参数构建）
   * @param name - 元素名称
   */
  const executePreCreate = (name?: string): void => {
    if (!needRecaption() || !('optionalAssets' in state)) {
      updateStep(2);
      return;
    }

    const params: Omit<MaterialsPreProcessElementData, 'url'> = {
      body: {
        components: (convertAssets2Components(state.optionalAssets) || []) as MaterialV1Component[],
        id: 'id' in state ? (state.id as string | undefined) : undefined,
        labels: state.labels,
        name: name ?? state.name,
        type: 'user',
        voice: isVideoMode ? undefined : 'voice' in state ? state.voice : undefined,
      },
    };
    doPreCreate(params);
  };

  const afterNameCheck = () => {
    if (needRecaption()) {
      executePreCreate(state.name);
    } else {
      updateStep(2);
    }
  };

  return {
    step,
    setStep: updateStep,
    preCreateLoading,
    updateCurrentAssets,
    afterNameCheck,
  };
};
